import { surveyQuestions, QuestionType } from '../../components/kyc-editor/question-types';

// Common types
export type LightColor = "green" | "yellow" | "red";
export type WithId = {
  id: string;
  displayTitle?: string;
  parameterDescription?: string;
};

// ===== Input Column Definitions =====

// Define the possible data sources and their available paths
export const morningstarDataPaths: Record<string, string> = {
  'assetAllocation.cash': 'Cash',
  'assetAllocation.usStocks': 'US Stocks',
  'assetAllocation.nonUSStocks': 'Non-US Stocks',
  'assetAllocation.bonds': 'Bonds',
  'assetAllocation.other': 'Other Assets',
  'effectiveBondDuration': 'Effective Bond Duration',
  'northAmericanEquityPercentage': 'North American Equity Percentage',
  'equityFlavor.largeGrowth': 'Large Growth',
  'equityFlavor.largeBlend': 'Large Blend',
  'equityFlavor.largeValue': 'Large Value',
  'equityFlavor.midGrowth': 'Mid Growth',
  'equityFlavor.midBlend': 'Mid Blend',
  'equityFlavor.midValue': 'Mid Value',
  'equityFlavor.smallGrowth': 'Small Growth',
  'equityFlavor.smallBlend': 'Small Blend',
  'equityFlavor.smallValue': 'Small Value',
};

// Populate surveyDataPaths directly from surveyQuestions
export const surveyDataPaths: Record<string, string> = Object.entries(surveyQuestions).reduce(
  (acc, [key, value]) => {
    acc[key] = value.question;
    return acc;
  },
  {} as Record<string, string>
);

// Helper type to extract survey questions by type
type SurveyQuestionsOfType<T extends QuestionType['type']> = {
  [K in keyof typeof surveyQuestions]: typeof surveyQuestions[K]['type'] extends T ? K : never
}[keyof typeof surveyQuestions];

// Numerical survey questions (number, slider, choiceNumber types)
export type NumericalSurveyKey = SurveyQuestionsOfType<'number' | 'slider' | 'choiceNumber'>;

// Categorical survey questions (text and multipleChoice types)
type CategoricalSurveyKey = SurveyQuestionsOfType<'text' | 'multipleChoice'>;

// Input column types using discriminated unions
export type MorningstarColumn = {
  type: 'numerical';
  source: 'morningstarData';
  path: keyof typeof morningstarDataPaths;
  displayName: string;
};

export type NumericalSurveyColumn = {
  type: 'numerical';
  source: 'surveyAnswers';
  path: NumericalSurveyKey;
  displayName: string;
};

export type CategoricalSurveyColumn = {
  type: 'categorical';
  source: 'surveyAnswers';
  path: CategoricalSurveyKey;
  displayName: string;
};

export type RatioSourceRef = 
  | { source: 'morningstarData'; path: keyof typeof morningstarDataPaths }
  | { source: 'surveyAnswers'; path: NumericalSurveyKey };

export type RatioColumn = {
  type: 'ratio';
  displayName: string;
  numerator: RatioSourceRef[];
  denominator: RatioSourceRef[];
  displayAsPercent?: true;
};

export type DerivedColumn = {
  type: 'derived';
  key: 'age' | 'timeHorizon';
  displayName: string;
};

export type LightStatusColumn = {
  type: 'lightStatus';
  parameterName: string; // Name of the issue flagger parameter to use
};

export type TagsColumn = {
  type: 'tags';
  tagID: string; // Name of the issue flagger parameter to use
};

// Union of all input column types
export type InputColumn = 
  | MorningstarColumn
  | NumericalSurveyColumn
  | CategoricalSurveyColumn
  | RatioColumn
  | DerivedColumn
  | LightStatusColumn
  | TagsColumn;

// ===== Decision Tree Nodes =====

// Define the output types for leaf nodes
export type IssueFlaggerOutput = {
  lightStatus?: LightColor;
  explanation?: string;
  tags?: string[]; // Tag IDs
};

export type StrategyRecommendationOutput = {
  strategyMenu?: string[]; // Ordered list of product tag IDs
  explanation?: string;
  extraTags?: string[]; // Additional tag IDs
};

// Generic leaf node type
export type LeafNode<T extends 'issueFlaggerLeaf' | 'strategyRecommendationLeaf', O> = {
  type: T;
  output: O;
};

// Specific leaf node types
export type IssueFlaggerLeafNode = LeafNode<'issueFlaggerLeaf', IssueFlaggerOutput>;
export type StrategyRecommendationLeafNode = LeafNode<'strategyRecommendationLeaf', StrategyRecommendationOutput>;

// To avoid circular references, we'll define interfaces instead of type aliases
export interface NumericalSplitNodeBase {
  type: 'numericalSplit';
  split: {
    columnIndex: number; // Index of the column in the inputColumns array
    ranges: Array<{
      maxValue: number; // The upper bound of this range (inclusive)
      childNode: any; // Will be properly typed in the specific implementations
    }>;
    defaultChild: any; // Will be properly typed in the specific implementations
  };
}

export interface CategoricalSplitNodeBase {
  type: 'categoricalSplit';
  split: {
    columnIndex: number; // Index of the column in the inputColumns array
    categoryMap: Array<{
      category: string; // The category value
      childNode: any; // Will be properly typed in the specific implementations
    }>;
    defaultChild: any; // Will be properly typed in the specific implementations
  };
}

// Update the base boolean split type to make trueChild optional
export interface BooleanSplitNodeBase {
  type: 'booleanSplit';
  split: {
    columnIndex: number; // Index of the column in the inputColumns array
    trueChild?: any; // Optional - will be properly typed in the specific implementations
    falseChild: any; // Will be properly typed in the specific implementations
  };
}

// Define the tree node types using interfaces to avoid circular reference errors
export interface IssueFlaggerNumericalSplitNode extends NumericalSplitNodeBase {
  split: {
    columnIndex: number;
    ranges: Array<{
      maxValue: number;
      childNode: IssueFlaggerTreeNode;
    }>;
    defaultChild: IssueFlaggerTreeNode;
  };
}

export interface IssueFlaggerCategoricalSplitNode extends CategoricalSplitNodeBase {
  split: {
    columnIndex: number;
    categoryMap: Array<{
      category: string;
      childNode: IssueFlaggerTreeNode;
    }>;
    defaultChild: IssueFlaggerTreeNode;
  };
}

// Update the specific boolean split node types
export interface IssueFlaggerBooleanSplitNode extends BooleanSplitNodeBase {
  split: {
    columnIndex: number;
    trueChild?: IssueFlaggerTreeNode; // Made optional
    falseChild: IssueFlaggerTreeNode;
  };
}

export interface StrategyRecommendationNumericalSplitNode extends NumericalSplitNodeBase {
  split: {
    columnIndex: number;
    ranges: Array<{
      maxValue: number;
      childNode: StrategyRecommendationTreeNode;
    }>;
    defaultChild: StrategyRecommendationTreeNode;
  };
}

export interface StrategyRecommendationCategoricalSplitNode extends CategoricalSplitNodeBase {
  split: {
    columnIndex: number;
    categoryMap: Array<{
      category: string;
      childNode: StrategyRecommendationTreeNode;
    }>;
    defaultChild: StrategyRecommendationTreeNode;
  };
}

// Update the specific boolean split node types
export interface StrategyRecommendationBooleanSplitNode extends BooleanSplitNodeBase {
  split: {
    columnIndex: number;
    trueChild?: StrategyRecommendationTreeNode; // Made optional
    falseChild: StrategyRecommendationTreeNode;
  };
}

// Define the tree node types using union types
export type IssueFlaggerTreeNode = 
  | IssueFlaggerNumericalSplitNode
  | IssueFlaggerCategoricalSplitNode
  | IssueFlaggerBooleanSplitNode
  | IssueFlaggerLeafNode;

export type StrategyRecommendationTreeNode = 
  | StrategyRecommendationNumericalSplitNode
  | StrategyRecommendationCategoricalSplitNode
  | StrategyRecommendationBooleanSplitNode
  | StrategyRecommendationLeafNode;

// ===== Parameter Types =====

// Define the parameter types with their specific tree node types
export type IssueFlaggerTable = WithId & {
  type: 'issueFlagger';
  inputColumns: InputColumn[];
  decisionTree: IssueFlaggerTreeNode;
};

export type IssueTriageTable = WithId & {
  type: 'issueTriage';
  issueFlaggerIds: string[]; // Ordered list of Issue Flagger table IDs
};

export type StrategyRecommendationTable = WithId & {
  type: 'strategyRecommendation';
  inputColumns: InputColumn[];
  decisionTree: StrategyRecommendationTreeNode;
};

// Union of all parameter types
export type Parameter = 
  | IssueFlaggerTable
  | IssueTriageTable
  | StrategyRecommendationTable;

// For backward compatibility or when the specific type doesn't matter
export type TreeNode = IssueFlaggerTreeNode | StrategyRecommendationTreeNode;

// ===== Inference Input Types =====

export type MorningstarData = {
  [key: string]: any;
  assetAllocation?: {
    cash?: number;
    usStocks?: number;
    nonUSStocks?: number;
    bonds?: number;
    other?: number;
  };
  effectiveBondDuration?: number;
  northAmericanEquityPercentage?: number;
  equityFlavor?: {
    largeGrowth?: number;
    largeBlend?: number;
    largeValue?: number;
    midGrowth?: number;
    midBlend?: number;
    midValue?: number;
    smallGrowth?: number;
    smallBlend?: number;
    smallValue?: number;
  };
};

export type SurveyData = {
  [K in keyof typeof surveyQuestions]?: any;
};
